// Per-tab auth token storage. Split out of SetupAxios.ts (which stays focused on axios instance
// creation + interceptor wiring) so this module owns one responsibility: where the active session
// token lives, and how a per-tab company/account override coexists with the shared base session.

export const AUTH_TOKEN = 'accessToken'

// Per-tab sentinel: set by logoutAuthToken() when THIS tab explicitly signs out while running
// under its own company-switch override. Distinguishes "this tab has no override, fall back to
// the shared base session" from "this tab just logged out, do NOT fall back" — without it,
// getAuthToken()'s fallback would silently re-authenticate a just-logged-out tab as the base
// company instead of actually signing it out.
const LOGGED_OUT_FLAG = 'numeo:tabLoggedOut'

// Per-tab active token: a company switch in CompanySwitcher writes to sessionStorage only, so it
// never crosses tabs (unlike localStorage, which also fires a cross-tab `storage` event). A tab
// without its own override falls back to the shared localStorage token (the default company) —
// unless this tab explicitly logged out of its override (see LOGGED_OUT_FLAG), in which case it
// must stay signed out rather than silently resuming as the base company.
export const getAuthToken = () => {
    if (sessionStorage.getItem(LOGGED_OUT_FLAG)) return null
    return sessionStorage.getItem(AUTH_TOKEN) ?? localStorage.getItem(AUTH_TOKEN)
}

// Backstop for LOGGED_OUT_FLAG's unenforced invariant: it's only ever cleared by setAuthToken()/
// switchAuthToken(), so a hypothetical future sign-in path that establishes a session another way
// (or sets Redux auth state and leans on a later verifyAuth() to pick it up) would leave a tab
// resolving null via getAuthToken() forever, with a valid base session sitting untouched in
// localStorage — fixable only by closing the tab. Called on the sign-in page's mount as a
// self-healing backstop; safe to call unconditionally, since landing on sign-in means this tab
// isn't relying on a live override anyway.
export const clearLoggedOutFlag = () => sessionStorage.removeItem(LOGGED_OUT_FLAG)

// True when this tab has switched to a different company/account than the shared base session.
// Storage that doesn't understand per-tab scoping — e.g. `loadops_auth_data`, which bridges to the
// browser extension and a few raw-`localStorage` readers (OnboardingAuthGuard, clientApiExtension)
// — must skip writing entirely while this is true, not just leave the stale base value in place,
// or the overridden identity leaks into those readers even though the JWT itself stayed scoped.
export const hasSessionOverride = (): boolean => sessionStorage.getItem(AUTH_TOKEN) !== null

// Per-tab sentinel for the "silently relocated back to the base company" notice: a switched tab's
// override 401ing falls back to the base session with a hard `window.location.replace` — the user
// was on company B and, with no visible cue, finds themselves back on their base company. A toast
// shown right before that replace() would be destroyed by the navigation before it's readable, so
// the flag survives the reload in sessionStorage (same-tab, same-origin navigations preserve it)
// and AuthInitializer shows the toast once, after the reload lands, then clears the flag.
const RELOCATION_NOTICE_FLAG = 'numeo:pendingRelocationNotice'
export const markPendingRelocationNotice = () => sessionStorage.setItem(RELOCATION_NOTICE_FLAG, '1')
export const consumePendingRelocationNotice = (): boolean => {
    const pending = sessionStorage.getItem(RELOCATION_NOTICE_FLAG) !== null
    if (pending) sessionStorage.removeItem(RELOCATION_NOTICE_FLAG)
    return pending
}

// Invalidate the SPECIFIC token that just failed (e.g. the one on the request that got a 401),
// not "whatever this tab currently has." Deciding by identity — rather than by re-reading
// "does an override currently exist" — is what makes this safe when two requests using the same
// now-invalid token race to failure at once (e.g. a REST call and the socket connection both
// rejecting around the same JWT expiry): the first call to observe the override token clears it;
// a naive second call would then see no override left and wrongly conclude it's safe to also wipe
// localStorage — the base session every other open tab depends on via getAuthToken()'s fallback.
// Comparing the failed token against the CURRENT contents of each storage sidesteps that entirely:
// whichever call runs, it only ever removes the slot the failed token still actually occupies, so
// a second call for the same already-cleared token finds it nowhere and correctly no-ops.
export const clearAuthToken = (failedToken: string) => {
    if (sessionStorage.getItem(AUTH_TOKEN) === failedToken) {
        sessionStorage.removeItem(AUTH_TOKEN)
        return
    }
    if (localStorage.getItem(AUTH_TOKEN) === failedToken) {
        localStorage.removeItem(AUTH_TOKEN)
    }
}

// Establish a new base session (fresh login, signup, OAuth/invite/QR/token-handoff completion,
// etc.). Every one of these flows must go through here rather than writing localStorage directly:
// getAuthToken() prefers a per-tab sessionStorage override, so without clearing it here, a tab
// that previously switched company would keep sending requests under the stale overridden token
// even after the user authenticates as a different account/company in that same tab.
export const setAuthToken = (token: string) => {
    localStorage.setItem(AUTH_TOKEN, token)
    sessionStorage.removeItem(AUTH_TOKEN)
    sessionStorage.removeItem(LOGGED_OUT_FLAG)
}

// Switch the active company for this tab only. Always sessionStorage, never localStorage — a
// localStorage write is shared across every open tab (and fires a cross-tab `storage` event),
// which is exactly the bug this whole per-tab-token design exists to avoid. Route every "switch to
// a different company" entry point (CompanySwitcher, AddCompanyModal, AcceptInvitationModal, ...)
// through here rather than inlining `sessionStorage.setItem` so a future 4th entry point can't
// accidentally reintroduce a raw localStorage write.
export const switchAuthToken = (token: string) => {
    sessionStorage.setItem(AUTH_TOKEN, token)
    sessionStorage.removeItem(LOGGED_OUT_FLAG)
}

// Ends THIS tab's session only. If this tab was running under its own company-switch override
// (hasSessionOverride()), the shared base token other tabs depend on via getAuthToken()'s fallback
// must be left untouched — only a local flag is set so THIS tab stops resolving a token (see
// LOGGED_OUT_FLAG on getAuthToken()), instead of silently falling back to the base company the
// user never asked to log out of. If this tab IS the base session (no override), there's nothing
// to distinguish it from "the shared session" — clearing it for real matches prior behavior and is
// the only way every tab still pinned to that same base session actually gets signed out too.
export const logoutAuthToken = () => {
    // Must be checked before hasSessionOverride(): a repeat call in the same tab (e.g. a fast
    // double-click on "Log out") runs after the override was already cleared by the first call, so
    // hasSessionOverride() would read false and fall through to the base-session branch below —
    // wiping the shared localStorage token every sibling tab depends on. The flag makes "this tab
    // already logged out of its override" durable across repeat calls, not just inferred from
    // whether the override token still happens to be present.
    if (sessionStorage.getItem(LOGGED_OUT_FLAG)) return
    if (hasSessionOverride()) {
        sessionStorage.removeItem(AUTH_TOKEN)
        sessionStorage.setItem(LOGGED_OUT_FLAG, '1')
    } else {
        localStorage.removeItem(AUTH_TOKEN)
    }
}

// Strip the path down to its ObjectId-free segments before a post-switch hard navigation, so
// landing back on e.g. `/loads/<id>` under the newly selected company — which doesn't have that
// entity — doesn't 404. Deliberately drops the ENTIRE query string and hash rather than trying to
// selectively strip entity-scoped params: those show up in many non-ObjectId shapes (numeric ids,
// external/DAT posting ids, etc.) that a targeted pattern would miss, so dropping everything is the
// safer default — this matches CompanySwitcher's original (pre-this-refactor) behavior.
export const stripEntityIdFromPath = (): string => window.location.pathname.replace(/\/[a-f0-9]{24}(\/|$)/gi, '/').replace(/\/+$/, '') || '/'

// Every company-switch entry point (CompanySwitcher, AddCompanyModal, AcceptInvitationModal, ...)
// must clear these after swapping the token: tour enablement is global but the per-tour "seen"
// keys are per app+user, so without this the signup tours would replay on every company switched
// to. Same for the mandatory company-details gate — letting it fire on a different company would
// write company info to the wrong app.
export const clearSignupGates = () => {
    try {
        localStorage.removeItem('numeo:tmsPageToursEnabled')
        localStorage.removeItem('numeo:tmsCategoryIntroEnabled')
        localStorage.removeItem('numeo:tmsCompanyDetailsPending')
    } catch {
        // localStorage blocked — gates already fail closed in their readers
    }
}

// Decode a JWT's payload. JWTs are base64URL-encoded (`-`/`_` instead of `+`/`/`, no padding) and
// UTF-8, so a raw `atob()` throws on tokens containing base64url-only characters and mangles any
// non-ASCII payload byte. Returns undefined on any malformed input rather than throwing, so callers
// can treat "can't decode" as "can't confirm" instead of crashing. Shared by every place that needs
// to peek at a JWT/ID-token payload client-side (cross-tab account-change detection here, and the
// Google-credential decoding in Login.tsx / the extension-signup and updater-lite SignUp pages).
export const decodeJwtPayload = (token: string): Record<string, unknown> | undefined => {
    try {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        )
        return JSON.parse(jsonPayload)
    } catch {
        return undefined
    }
}

// Standard OIDC claims Google's Identity Services credential carries under the default
// `openid email profile` scopes the Google Sign-In button requests. `email`/`sub` are the only
// fields any caller actually reads, so only those are validated below; the rest are optional
// since some Google accounts omit them (e.g. no profile photo set).
export interface GoogleCredentialPayload {
    email: string
    sub: string
    name?: string
    email_verified?: boolean
    picture?: string
}

// Typed wrapper around decodeJwtPayload for Google Sign-In's credential (ID token) — the one
// caller-side shape decodeJwtPayload's generic Record<string, unknown> gets narrowed to across
// Login.tsx / extension-signup's SignUp.tsx / updater-lite's SignUp.tsx, all of which previously
// duplicated an `: any`-typed local decodeJwtResponse. Throws (rather than returning undefined)
// on a malformed credential so callers can keep handling it in their existing try/catch around
// handleGoogleSuccess, matching the pre-consolidation raw-atob() behavior of throwing synchronously.
export const decodeGoogleCredential = (token: string): GoogleCredentialPayload => {
    const payload = decodeJwtPayload(token)
    if (!payload || typeof payload.email !== 'string' || typeof payload.sub !== 'string') {
        throw new Error('Invalid Google credential token')
    }
    return payload as unknown as GoogleCredentialPayload
}

// The shape this module actually reads off a failed request — config.headers can be an
// AxiosHeaders instance (case-insensitive `.get()`) or a plain object, depending on how the
// request was built, so both are accepted here rather than requiring the full AxiosError type.
type ErrorWithAuthHeader = { config?: { headers?: { get?: (name: string) => string | undefined; Authorization?: string } } }

// Recover the token that was actually sent on a failed request, so clearAuthToken() can invalidate
// that specific token rather than "whatever the tab currently has." Tries AxiosHeaders' case-
// insensitive `.get()` first, falling back to direct property access.
export const bearerTokenFrom = (err: unknown): string | undefined => {
    const headers = (err as ErrorWithAuthHeader)?.config?.headers
    const header = headers?.get?.('Authorization') ?? headers?.Authorization
    return header?.replace(/^Bearer\s+/, '')
}

// Shared 401-handling core for both axios instances in SetupAxios.ts. Clears whichever token
// actually failed and reports whether the shared base session still resolves afterward, so the
// caller can fall back to it (reload) instead of forcing a full sign-out when only a per-tab
// override was invalid.
//
// Deliberately does NOT fall back to a blind `getAuthToken()` guess when the failed token can't be
// recovered from the request — that read happens at 401-handling time, which can race a company
// switch: `switchAuthToken()` writes the new override synchronously before the hard
// `window.location.replace` actually unloads the page, so a request that failed under the OLD
// token could momentarily see the NEW one and clear that instead, defeating the whole point of
// identity-based clearing. Guessing "this tab's own override" is safe regardless of that race
// (it's never the shared base token by construction, so it can't wipe another tab's session); with
// no recoverable identity and no override to safely guess at, treat it as a real base-session
// failure rather than risk leaving a known-bad token in place.
export const clearFailedTokenAndCheckBaseSurvives = (err: unknown): boolean => {
    const failedToken = bearerTokenFrom(err)
    if (failedToken) {
        clearAuthToken(failedToken)
    } else if (hasSessionOverride()) {
        clearAuthToken(sessionStorage.getItem(AUTH_TOKEN) ?? '')
    } else if (sessionStorage.getItem(LOGGED_OUT_FLAG)) {
        // This tab already explicitly logged out of its own override (logoutAuthToken) — a stray
        // request that slips out afterward (e.g. an in-flight poller/socket-reconnect that hasn't
        // unmounted yet) has no token to attach by construction, so an unrecoverable 401 here isn't
        // evidence the shared base session died. Nothing to clear here: the trailing getAuthToken()
        // check below is already null via its own LOGGED_OUT_FLAG short-circuit. Must NOT wipe
        // localStorage — that would reintroduce the exact "switched tab's logout kills sibling
        // tabs" bug via this side door.
    } else {
        // No recoverable identity and no override to guess at — per the comment above, this is
        // treated as a real base-session failure, so the base token must actually be cleared here
        // (not just reported as gone) or a known-bad token survives in localStorage after the
        // caller redirects to sign-in.
        localStorage.removeItem(AUTH_TOKEN)
    }
    return getAuthToken() !== null
}
